import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Table,
  UxTooltip,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button
} from "botbit-ui-components";
import { useDispatch, useSelector } from 'react-redux';
import translate from "../../../config/translations/translate";
import { setLoading } from '../../../commons/components/application/miscActions';
import { setReviewLeaderboardOptInStatus } from '../../../commons/components/application/applicationActions';
import './CompanyUsersDownload.scss';
import { 
  startCompanyUsersDownloadProcessByStoreId,
  getCompanyUsersDownloadStatusByStoreId,
  getCompanyUsersDownloadCSVByStoreId,
  getCompanyOptinStatusReviewLeaderboard,
  updateCompanyOptinStatusReviewLeaderboard
} from '../../../api/dashboardV2Api';
import JSZip from 'jszip';
import withEventTracking, { trackers } from '../../../config/withEventTracking';
import MobileScreenUnavailableMessage from '../../../commons/components/application/MobileScreenUnavailableMessage';
import { useHistory } from 'react-router-dom';

const CompanyUsersDownload = ({ t, trackEvent }) => {
  const dispatch = useDispatch();
  const selectedCompany = useSelector(state => state.application.selectedCompany);
  const admin = useSelector(state => state.login.admin);
  const availableStores = useSelector(state => state.application.availableStores);
  const loading = useSelector(state => state.misc.loading);
  const reviewLeaderboardOptInStatus = useSelector(state => state.application.reviewLeaderboardOptInStatus);
  const [downloadProcesses, setDownloadProcesses] = useState({});
  const [activeStores, setActiveStores] = useState([]);
  const [showStartAllButton, setShowStartAllButton] = useState(false);
  const [showDownloadAllButton, setShowDownloadAllButton] = useState(false);
  const [showDataCaptureAlert, setShowDataCaptureAlert] = useState(false);
  const [isDataCaptureModalOpen, setIsDataCaptureModalOpen] = useState(false);
  const pollingIntervalsRef = useRef({});
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);  
  const [isMobileModalOpen, setIsMobileModalOpen] = useState(window.innerWidth < 1050);
  const history = useHistory();
  useEffect(() => {
    initializeComponent();
    
    // Verificar optin solo si no está ya en Redux
    if (reviewLeaderboardOptInStatus === null || reviewLeaderboardOptInStatus === undefined) {
      checkOptinStatus();
    }
  }, []);

  useEffect(() => {
    setShowDataCaptureAlert(!reviewLeaderboardOptInStatus);
  }, [reviewLeaderboardOptInStatus]);

  useEffect(() => {
    const processStates = Object.values(downloadProcesses);
    
    // Mostrar botón "Iniciar todos" si hay al menos un proceso no iniciado
    const hasNotStartedProcesses = processStates.some(process => 
      process.state === 'NOT_STARTED'
    );
    setShowStartAllButton(hasNotStartedProcesses);
    
    // Mostrar botón "Descargar todos" si hay al menos un proceso completado
    const hasCompletedProcesses = processStates.some(process => 
      process.state === 'FINISHED_OK'
    );
    setShowDownloadAllButton(hasCompletedProcesses);
  }, [downloadProcesses]);

  // Efecto de limpieza para limpiar intervalos cuando el componente se desmonte
  useEffect(() => {
    return () => {
      // Limpiar todos los intervalos activos al desmontar el componente
      Object.values(pollingIntervalsRef.current).forEach(interval => {
        clearInterval(interval);
      });
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
        setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
        window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (windowWidth < 1050) {
      setIsMobileModalOpen(true);
    } else {
      setIsMobileModalOpen(false);
    }
  }, [windowWidth]);

  const initializeComponent = async () => {
    dispatch(setLoading(true));
    try {
      // Primero obtener las tiendas activas
      if(admin.objectAccess === "ALL" || admin.objectAccess === "COMPANY") {
        const filteredStores = availableStores.filter(store => store.companyId === selectedCompany.id);
        setActiveStores(filteredStores);
         // Luego verificar el estado de cada tienda
         await checkAllStoresStatus(filteredStores);
      } else {
        setActiveStores(admin.stores);
        // Luego verificar el estado de cada tienda
        await checkAllStoresStatus(admin.stores);
      }  
      
    } catch (error) {
      console.error("Error initializing component:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const checkAllStoresStatus = async (stores) => {
    const updatedProcesses = {};
    
    // Hacer peticiones en paralelo para verificar el estado de cada tienda
    const statusPromises = stores.map(async (store) => {
      try {
        const statusResponse = await getCompanyUsersDownloadStatusByStoreId(store.id);

        if(statusResponse.data.data === null) {
          updatedProcesses[store.id] = {
            state: 'NOT_STARTED',
            progress: 0,
            usersProcessed: 0,
          };
        } else {

          const { state = 'IN_PROGRESS' , progress = 0 } = statusResponse.data.data;

          const usersProcessed = statusResponse.data.data.results.userCount;

          updatedProcesses[store.id] = {
            state,
            progress,
            usersProcessed,
          };
          
          // Si el proceso está en progreso, iniciar polling
          if ( progress < 100) {
            startPolling(store.id);
          } else if ( progress === 100) {
           updatedProcesses[store.id] = {
            state: 'FINISHED_OK',
            progress: 100,
            usersProcessed,
           };
          }
        }
        
      } catch (error) {
        console.error(`Error checking status for store ${store.id}:`, error);
        // Si hay error al verificar el estado, asumir que no hay proceso iniciado
        updatedProcesses[store.id] = {
          state: 'NOT_STARTED',
          progress: 0,
          usersProcessed: 0,
        };
      }
    });
    
    // Esperar a que todas las peticiones se completen
    await Promise.all(statusPromises);
    
    // Actualizar el estado con todos los procesos
    setDownloadProcesses(updatedProcesses);
  };

  const getActiveStores = useCallback(async (page, order, filters) => {
    return {
      data: {
        data: activeStores
      }
    };
  }, [activeStores]);

  const handleStartProcess = async (storeId) => {
    try {
      // Actualizar estado local inmediatamente para feedback visual
      setDownloadProcesses(prev => ({
        ...prev,
        [storeId]: {
          ...prev[storeId],
          state: 'IN_PROGRESS',
          progress: 0,
          usersProcessed: prev[storeId] && prev[storeId].usersProcessed ? prev[storeId].usersProcessed : 0
        }
      }));
      
      // Iniciar el proceso en el backend
      await startCompanyUsersDownloadProcessByStoreId(storeId);
      
      // Iniciar polling para actualizar el progreso
      startPolling(storeId);
      
    } catch (error) {
      console.error("Error starting download process:", error);
      
      // Revertir al estado anterior en caso de error
      setDownloadProcesses(prev => ({
        ...prev,
        [storeId]: {
          ...prev[storeId],
          state: 'ERROR',
        }
      }));
    }
  };

  const startPolling = (storeId) => {
    // Si ya hay un polling activo para esta tienda, no crear otro
    if (pollingIntervalsRef.current[storeId]) {
      return;
    }
    
    const pollInterval = setInterval(async () => {
      try {
        const response = await getCompanyUsersDownloadStatusByStoreId(storeId);
        if (response && response.data && response.data.data) {
            const { state = 'IN_PROGRESS', progress } = response.data.data;
            const usersProcessed = response.data.data.results.userCount;
            
            setDownloadProcesses(prev => ({
              ...prev,
              [storeId]: {
                ...prev[storeId],
                state,
                progress,
                usersProcessed,
              }
            }));
            
            // Si el proceso ha finalizado, detener el polling
            if (state === 'FINISHED_OK' || progress === 100) {
              setDownloadProcesses(prev => ({
                ...prev,
                [storeId]: {
                  ...prev[storeId],
                  state: 'FINISHED_OK',
                  progress: 100,
                  usersProcessed,
                }
              }));
              clearInterval(pollInterval);
              delete pollingIntervalsRef.current[storeId];
            }
        } else {
            console.warn('Response data is null or undefined:', response);
            return;
        }
        
      } catch (error) {
        console.error("Error checking download status:", error);
        clearInterval(pollInterval);
        delete pollingIntervalsRef.current[storeId];
      }
    }, 10000); // Verificar cada 10 segundos
    
    // Guardar el ID del intervalo
    pollingIntervalsRef.current[storeId] = pollInterval;
  };

  // Función auxiliar para realizar la descarga
  const handleDownloadSingle = async (storeName, storeId, shouldResetState = true) => {
    try {
      // Llamar a la API para obtener el CSV como blob
      const response = await getCompanyUsersDownloadCSVByStoreId(storeId);
      // Crear un blob con el contenido CSV
      const blob = new Blob([response.data], { type: "text/csv" });
      
      // Verificar compatibilidad con navegadores antiguos (IE)
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        const fileName = `usuarios_${storeName.replace(/\s+/g, '_')}.csv`;
        window.navigator.msSaveOrOpenBlob(blob, fileName);
      } else {
        // Crear un enlace temporal para la descarga
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `usuarios_${storeName.replace(/\s+/g, '_')}.csv`;
        
        // Agregar al DOM, hacer clic y remover
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar la URL del objeto para liberar memoria
        window.URL.revokeObjectURL(url);
        trackEvent('Company_users_download_store', {
          storeId: storeId,
          companyId: selectedCompany.id
        }, false, [trackers.MIXPANEL, trackers.INTERCOM]);
        // Solo resetear el estado si se indica (para descargas individuales)
        if (shouldResetState) {
          setDownloadProcesses(prev => ({
            ...prev,
            [storeId]: {
              state: 'NOT_STARTED',
              progress: 0,
              usersProcessed: 0,
            }
          }));
        }
      }
      
    } catch (error) {
      console.error('Error descargando el CSV:', error);
      // Aquí podrías mostrar un mensaje de error al usuario
      alert(t('alerts.downloadError') || 'Error al descargar el archivo. Por favor, inténtalo de nuevo.');
    }
  };

  // Función para descargas individuales (mantiene compatibilidad)
  const handleDownload = async (storeName, storeId) => {
    return handleDownloadSingle(storeName, storeId, true);
  };

  const handleStartAllProcesses = () => {
    const storesWithoutProcess = Object.keys(downloadProcesses).filter(
      storeId => downloadProcesses[storeId].state === 'NOT_STARTED'
    );
    
    // Iniciar procesos para todas las tiendas que no tienen proceso iniciado
    storesWithoutProcess.forEach(storeId => {
      handleStartProcess(parseInt(storeId));
    });
  };
  
  const handleDownloadAll = async () => {
    const completedStores = Object.keys(downloadProcesses).filter(
      storeId => downloadProcesses[storeId].state === 'FINISHED_OK'
    );
    
    if (completedStores.length === 0) {
      alert(t('alerts.noCompletedProcesses'));
      return;
    }

    try {
      dispatch(setLoading(true));
      const zip = new JSZip();

      const downloadPromises = completedStores.map(async (storeId) => {
        try {
          const store = activeStores.find(store => store.id === parseInt(storeId));
          const response = await getCompanyUsersDownloadCSVByStoreId(storeId);
          
          const fileName = `usuarios_${store.name.replace(/\s+/g, '_')}.csv`;
          zip.file(fileName, response.data);
          
          return true;
        } catch (error) {
          console.error(`Error downloading CSV for store ${storeId}:`, error);
          return false;
        }
      });

      const results = await Promise.all(downloadPromises);
      
      if (results.some(result => !result)) {
        alert(t('alerts.someDownloadsError'));
      }

      const zipBlob = await zip.generateAsync({
        type: "blob",
        compression: "DEFLATE",
        compressionOptions: {
          level: 9
        }
      });

      const url = window.URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `usuarios_todas_las_sucursales.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      trackEvent('Company_users_download_all', {
        companyId: selectedCompany.id,
      }, false, [trackers.MIXPANEL, trackers.INTERCOM]);


    } catch (error) {
      console.error('Error creating ZIP file:', error);
      alert(t('alerts.downloadZipError'));
    } finally {
      dispatch(setLoading(false));
    }
    
    // Al final, resetear todos los estados a NOT_STARTED
    const resetStates = {};
    completedStores.forEach(storeId => {
      resetStates[storeId] = {
        state: 'NOT_STARTED',
        progress: 0,
        usersProcessed: 0,
      };
    });
    
    setDownloadProcesses(prev => ({
      ...prev,
      ...resetStates
    }));
  };

  const checkOptinStatus = async () => {
    try {
      const response = await getCompanyOptinStatusReviewLeaderboard(selectedCompany.id);
      const stores = response.data.data; // Array de StoreBasicDTO
      
      // Si alguna tienda tiene el flag en false, el estado debe ser true (mostrar alerta)
      // Si todas las tiendas tienen el flag en true, el estado debe ser false (no mostrar alerta)
      const hasStoreWithOptinDisabled = stores.some(store => store.reviewLeaderboardOptInEnabled === false);
      
      dispatch(setReviewLeaderboardOptInStatus(hasStoreWithOptinDisabled));
    } catch (error) {
      console.error("Error checking optin status:", error);
    }
  };

  const handleActivateDataCapture = async () => {
    try {
      dispatch(setLoading(true));
      
      setIsDataCaptureModalOpen(false);
      
      
    } catch (error) {
      console.error("Error activating data capture:", error);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleCancelDataCapture = () => {
    setIsDataCaptureModalOpen(false);
  };

  const renderActionButton = (row) => {
    const storeProcess = downloadProcesses[row.id] || { 
      state: row.processState || 'NOT_STARTED', 
      progress: row.processProgress || 0,
    };

    switch (storeProcess.state) {
      case 'FINISHED_OK':
        return (
          <button 
            className="download-button download-ready"
            onClick={() => handleDownload(row.name, row.id)}
          >
            {t('buttons.download')} {`(${storeProcess.usersProcessed} ${t('buttons.users')})`}
          </button>
        );
      
      case 'IN_PROGRESS':
        return (
          <button 
            className="download-button download-in-progress"
            disabled
          >
            {t('buttons.inProgress', { progress: storeProcess.progress })}
          </button>
        );
      
      case 'ERROR':
        return (
          <button 
            className="download-button download-error"
            onClick={() => handleStartProcess(row.id)}
          >
            {t('buttons.retry')}
          </button>
        );
      
      default:
        return (
          <button 
            className="download-button download-start"
            onClick={() => handleStartProcess(row.id)}
          >
            {t('buttons.startProcess')}
          </button>
        );
    }
  };

  const columns = [
    {
      dataField: 'name',
      title: (
        <div className="column-with-tooltip">
          {t('columns.storeName')}
          <UxTooltip
            message={t('columns.storeName.tooltip.content')}
            position="top"
          />
        </div>
      ),
      size: 45,
      formatter: (cellValue) => (
        <span className="store-name">{cellValue}</span>
      ),
    },
    {
      dataField: 'actions',
      title: t('columns.actions'),
      size: 23,
      formatter: (cellValue, row) => renderActionButton(row),
    }
  ];

  return (
    <>
      <MobileScreenUnavailableMessage isOpen={isMobileModalOpen} />
      <div className="company-users-download-container">
        <div className="company-users-download-header">
          <h1 className="company-title">{t('title')}</h1>
          <p className="company-description">{t('description')}</p>
        </div>

        {showDataCaptureAlert && (
        <div className="data-capture-alert">
          <div className="alert alert-danger" role="alert">
            <strong>{t('alerts.dataCapture.title')}</strong> {t('alerts.dataCapture.message')}
            <button 
              className="btn btn-link p-0 ml-1"
              onClick={() => history.push('/review-leaderboard/config')}
              style={{ color: 'inherit', textDecoration: 'underline' }}
            >
              {t('alerts.dataCapture.clickHere')}
            </button>
          </div>
        </div>
      )}
        
        <div className="company-users-table-section">
          <div className="company-users-table-header">
            <h2>{t('tableTitle')}</h2>
            <div className="header-actions">
              {showStartAllButton && (
                <button 
                  className="action-button start-all-button"
                  onClick={handleStartAllProcesses}
                >
                  {t('buttons.startAllProcesses')}
                </button>
              )}
              {showDownloadAllButton && (
                <button 
                  className="action-button download-all-button"
                  onClick={handleDownloadAll}
                >
                  {t('buttons.downloadAll')}
                </button>
              )}
            </div>
          </div>
          
          <div className="company-users-table-container">
            {loading ? (
              <div className="loading-message">{t('loading')}</div>
            ) : (
              <Table
                getData={getActiveStores}
                columns={columns}
                showMobileColumnTitles={true}
                dataPath="data.data"
                elementKey="id"
                pagination={false}
                filterValues={{}}
              />
            )}
          </div>
        </div>
      </div>

      {/* Modal para activar captación de datos */}
      <Modal isOpen={isDataCaptureModalOpen} toggle={() => setIsDataCaptureModalOpen(false)}>
        <ModalHeader toggle={() => setIsDataCaptureModalOpen(false)}>
          {t('modal.dataCapture.title')}
        </ModalHeader>
        <ModalBody>
          <p>{t('modal.dataCapture.message')}</p>
        </ModalBody>
        <ModalFooter>
          <Button 
            className="btn btn-secondary"
            onClick={handleCancelDataCapture}
          >
            {t('modal.dataCapture.cancel')}
          </Button>
          <Button 
            className="btn btn-primary"
            onClick={handleActivateDataCapture}
          >
            {t('modal.dataCapture.accept')}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

CompanyUsersDownload.translations = {
  title: {
    es: "Descarga de usuarios por sucursal",
    en: "Download users by store"
  },
  description: {
    es: "En esta sección puedes descargar la información de los usuarios registrados en cada sucursal de tu compañía.",
    en: "In this section you can download the information of registered users for each store in your company."
  },
  tableTitle: {
    es: "Sucursales disponibles",
    en: "Available stores"
  },
  tableTooltip: {
    title: {
      es: "Información de descarga",
      en: "Download information"
    },
    content: {
      es: "Inicia el proceso de descarga para cada sucursal. Una vez completado, podrás descargar un archivo CSV con la información de los usuarios.",
      en: "Start the download process for each store. Once completed, you can download a CSV file with user information."
    }
  },
  loading: {
    es: "Cargando datos...",
    en: "Loading data..."
  },
  columns: {
    storeName: {
      es: "Nombre de la sucursal",
      en: "Store name",
      tooltip: {
        content: {
          es: "Nombre de la sucursal",
          en: "Store name"
        }
      }
    },
    usersCount: {
      es: "Cantidad de usuarios",
      en: "Number of users",
      tooltip: {
        content: {
          es: "Número total de usuarios registrados en esta sucursal",
          en: "Total number of users registered in this store"
        }
      }
    },
    actions: {
      es: "Acciones",
      en: "Actions"
    }
  },
  buttons: {
    startProcess: {
      es: "Iniciar descarga",
      en: "Start download"
    },
    inProgress: {
      es: "En proceso... {{progress}}%",
      en: "In progress... {{progress}}%"
    },
    download: {
      es: "Descargar",
      en: "Download"
    },
    retry: {
      es: "Reintentar",
      en: "Retry"
    },
    startAllProcesses: {
      es: "Iniciar todas las descargas",
      en: "Start all downloads"
    },
    downloadAll: {
      es: "Descargar todos",
      en: "Download all"
    },
    users: {
      es: "usuarios",
      en: "users"
    } 
  },
  alerts: {
    noCompletedProcesses: {
      es: "No hay procesos completados para descargar",
      en: "There are no completed processes to download"
    },
    downloadError: {
      es: "Error al descargar el archivo. Por favor, inténtalo de nuevo.",
      en: "Error downloading the file. Please try again."
    },
    someDownloadsError: {
      es: "Algunos archivos no pudieron ser descargados",
      en: "Some files could not be downloaded"
    },
    downloadZipError: {
      es: "Error al crear el archivo ZIP. Por favor, inténtalo de nuevo.",
      en: "Error creating ZIP file. Please try again."
    },
    dataCapture: {
      title: {
        es: "¡Atención!",
        en: "Attention!"
      },
      message: {
        es: "Algunas de las tiendas de tu empresa no están captando datos de los usuarios que dejan una reseña usando el QR de RAY. Para activar la captación de datos, ",
        en: "Some of your company's stores are not capturing data from users who leave a review using RAY's QR code. To activate data capture, "
      },
      clickHere: {
        es: "haz click aquí",
        en: "click here"
      }
    }
  },
  modal: {
    dataCapture: {
      title: {
        es: "Activar captación de datos",
        en: "Activate data capture"
      },
      message: {
        es: "Al encender esta configuración los clientes que escaneen el QR verán un botón de 'Login con Google' antes de dejar su reseña.",
        en: "When turning on this configuration, customers who scan the QR will see a 'Login with Google' button before leaving their review."
      },
      cancel: {
        es: "Cancelar",
        en: "Cancel"
      },
      accept: {
        es: "Acepto, volver a encender la captación de datos",
        en: "I accept, turn on data capture again"
      }
    }
  }
};

export default withEventTracking(translate(CompanyUsersDownload));
